import { sanitiseUrlForEnvironment, trimTrailingSlash, useLocalisedLink, useTenant } from '@parcel2go/npm-p2g-app';
import { useMemo } from 'react';
import { PAYMENT_PATH } from '~/lib/constants';
import { TenantSettingKeys } from '~/lib/consts/tenant-setting-keys';

/**
 * Which purchase journey the payment link belongs to. Each journey can be migrated to the new
 * (order-process) payment page on its own, ahead of the tenant-wide flag.
 */
export type PaymentJourney = 'prepay' | 'surcharge';

const JOURNEY_SETTING_KEYS: Partial<Record<PaymentJourney, string>> = {
  prepay: TenantSettingKeys.Payments.PrePayEnabled
};

/**
 * Resolves the base payment page URL for a journey - the new order-process page when new payments
 * are enabled, otherwise the legacy `/order/payment` page.
 *
 * New payments are on when the tenant-wide flag is set OR the journey's own flag is set, so a
 * journey can be switched over without moving the others. `surcharge` has no journey flag of its
 * own and follows the tenant-wide flag alone.
 */
export function usePaymentPath(journey: PaymentJourney = 'surcharge') {
  const { settings } = useTenant();
  const { swap } = useLocalisedLink();

  const paymentPath = useMemo(() => {
    const baseUrl = trimTrailingSlash(settings[TenantSettingKeys.Website] || '');
    const journeyKey = JOURNEY_SETTING_KEYS[journey];
    const newPaymentsEnabled =
      settings[TenantSettingKeys.Payments.Enabled] === 'true' || (!!journeyKey && settings[journeyKey] === 'true');

    if (newPaymentsEnabled) {
      return swap(sanitiseUrlForEnvironment(`${baseUrl}${PAYMENT_PATH}`));
    } else {
      return swap(sanitiseUrlForEnvironment(`${baseUrl}/order/payment`));
    }
  }, [settings, swap, journey]);

  return paymentPath;
}
